// ViewReturn.jsx
import * as React from "react";
import axios from "axios";
import Header from "../../components/Header";
import SimpleTable from "../../components/SimpleTable";
import {
  Box,
  Snackbar,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Divider,
  TextField,
  MenuItem,
  Menu,
  useTheme,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EditIcon from "@mui/icons-material/Edit";
import { Formik, FieldArray } from "formik";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { tokens } from "../../theme";



/* ---------- DATE HELPERS (UTC-SAFE) ---------- */

// Display date without timezone conversion
const fmtDate = (iso) => {
  if (!iso) return "-";
  return iso.split("T")[0]; // YYYY-MM-DD
};

// For <input type="date">
const toDateInput = (iso) => {
  if (!iso) return "";
  return iso.split("T")[0]; // YYYY-MM-DD
};

// Send date back to backend in UTC midnight
const fromDateInput = (yyyyMmDd) => {
  if (!yyyyMmDd) return null;
  return `${yyyyMmDd}T00:00:00.000Z`;
};

// Your server accepted "YYYY-MM-DD" in the sample PUT, so we send that string as-is.


// keep selected id visible even if not in current options
const ensureOption = (list, id) => {
  if (id === "" || id === null || id === undefined) return list || [];
  const nid = Number(id);
  const exists = (list || []).some((o) => Number(o.id) === nid);
  return exists ? list : [{ id: nid, name: `#${nid}` }, ...(list || [])];
};

// normalize option arrays of various shapes to {id, name}
const normalizeList = (arr, nameKeys = ["name", "fullName", "title"]) => {
  if (!Array.isArray(arr)) return [];
  return arr
    .map((o) => {
      const id =
        o?.id ??
        o?.ID ??
        o?.Id ??
        o?.itemId ??
        o?.warehouseId ??
        o?.userId ??
        o?.uuid;
      const key = nameKeys.find((k) => o && o[k] != null);
      const name =
        o?.[key] ??
        o?.itemName ??
        o?.warehouseName ??
        o?.label ??
        `#${id ?? "?"}`;
      return { id, name };
    })
    .filter((o) => o.id !== undefined && o.id !== null);
};

/* ---------- API ---------- */
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL, // e.g. https://distributer.garamuletaapartment.com
  timeout: 20000,
  headers: {
    "Content-Type": "application/json",
    "Cache-Control": "no-cache",
    Pragma: "no-cache",
  },
});

async function getJSON(path, params) {
  try {
    const res = await api.get(path, {
      params: { _t: Date.now(), ...(params || {}) },
    });
    return { data: res.data, error: null };
  } catch (err) {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.errors ||
      err.message ||
      "Request failed";
    return { data: null, error: msg };
  }
}
async function putJSON(path, body) {
  try {
    const res = await api.put(path, body, {
      headers: { "Cache-Control": "no-cache" },
    });
    return { data: res.data, error: null };
  } catch (err) {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.errors ||
      err.message;
    return { data: null, error: msg };
  }
}
async function deleteJSON(path) {
  try {
    const res = await api.delete(path, {
      headers: { "Cache-Control": "no-cache" },
    });
    return { data: res.data, error: null };
  } catch (err) {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.errors ||
      err.message;
    return { data: null, error: msg };
  }
}

/* ---------- page ---------- */
export default function ViewReturn() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const lsUser = (() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  })();
  const roleRaw = String(
    lsUser?.role || lsUser?.userRole || lsUser?.roleName || lsUser?.type || ""
  )
    .toLowerCase()
    .trim();

  const isSalesMan =
    /(sales\s*-?\s*man|salesman|sales\s*person|salesperson|^sales$)/i.test(
      roleRaw
    );
  const currentUserId = String(
    lsUser?.id ?? lsUser?.userId ?? lsUser?._id ?? ""
  );
  const canEdit = !isSalesMan;

  // table data & ui
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [toast, setToast] = React.useState({
    open: false,
    msg: "",
    sev: "success",
  });

  // dialogs
  const [detailRow, setDetailRow] = React.useState(null);

  const [editOpen, setEditOpen] = React.useState(false);
  const [editRow, setEditRow] = React.useState(null);
  const [editValues, setEditValues] = React.useState({
    warehouseId: "",
    userId: "",
    status: "pending",
    reason: "",
    description: "",
    type: "",
    returnTo: "",
    returnDate: "",
    items: [],
  });
  const [saving, setSaving] = React.useState(false);

  const [deleteRow, setDeleteRow] = React.useState(null);
  const [deleting, setDeleting] = React.useState(false);

  // options for item, warehouse, user
  const [items, setItems] = React.useState([]);
  const [warehouses, setWarehouses] = React.useState([]);
  const [users, setUsers] = React.useState([]);
  const [optsLoading, setOptsLoading] = React.useState(false);

  // status inline menu
  const [statusMenu, setStatusMenu] = React.useState({
    anchorEl: null,
    row: null,
  });

  // fetch list (cache-busted)
  const load = React.useCallback(async () => {
    setLoading(true);

    // Build API URL based on user role
    let apiUrl = "/api/return";
    if (isSalesMan) {
      // Sales personnel can only see their own records - use the correct API endpoint
      apiUrl = `/api/return/user/${currentUserId}`;
    }
    // For non-sales users, use the default URL to get all records

    const { data, error } = await getJSON(apiUrl);
    setLoading(false);
    if (error) {
      setToast({ open: true, msg: "Failed to load returns", sev: "error" });
      return;
    }
    // normalize to include denormalized names for table convenience
    const mapped = (Array.isArray(data) ? data : []).map((r) => ({
      ...r,
      itemName: r?.Item?.name ?? "-",
      userName: r?.User?.fullName ?? "-",
      warehouseName: r?.Warehouse?.name ?? "-",
    }));
    setRows(mapped);
  }, [isSalesMan, currentUserId]);

  React.useEffect(() => {
    load();
  }, [load]);

  // fetch dropdown options
  const loadOptions = React.useCallback(async () => {
    setOptsLoading(true);
    const [itemRes, whRes, userRes] = await Promise.all([
      getJSON("/api/item"),
      getJSON("/api/warehouse"),
      getJSON("/api/user"),
    ]);

    setItems(normalizeList(itemRes.data));
    setWarehouses(normalizeList(whRes.data));

    // Filter to only sales users (same as CreateReturn)
    const salesUsers = Array.isArray(userRes.data)
      ? userRes.data.filter(
          (u) => (u.Role?.name || "").toLowerCase() === "sales"
        )
      : [];

    setUsers(normalizeList(salesUsers, ["fullName", "name", "title"]));
    setOptsLoading(false);
  }, []);

  React.useEffect(() => {
    // prefetch options once
    loadOptions();
  }, [loadOptions]);


  // Update the handleUpdate function to match your backend structure
  const handleUpdate = async (payload) => {
    if (!editRow?.id) return;

    // Validate that at least one item exists and all quantities are >= 1
    if (!payload.items || payload.items.length === 0) {
      setToast({
        open: true,
        msg: "At least one item is required",
        sev: "error",
      });
      return;
    }

    for (const item of payload.items) {
      const qty = Number(item.quantity);
      if (Number.isNaN(qty) || qty < 1) {
        setToast({
          open: true,
          msg: "Each item quantity must be ≥ 1",
          sev: "error",
        });
        return;
      }
      if (!item.itemId || item.itemId === "") {
        setToast({
          open: true,
          msg: "Please select an item for all rows",
          sev: "error",
        });
        return;
      }
    }

    setSaving(true);

    // Build payload exactly as your backend expects
    const apiPayload = {
      id: editRow.id, // Include ID if backend needs it
      userId: Number(payload.userId),
      warehouseId: Number(payload.warehouseId),
      reason: payload.reason || null,
      description: payload.description || null,
      type: payload.type || null,
      returnTo: payload.returnTo || null, // ✅ ADD THIS

      returnDate: fromDateInput(payload.returnDate),
      items: payload.items.map((it) => ({
        itemId: Number(it.itemId),
        quantity: Number(it.quantity),
      })),
      // Include status if you want to update it too
      status: payload.status || "pending",
    };

    // optimistic merge
    const prevRows = [...rows];

    // Update item names for display
    const updatedItemNames = apiPayload.items
      .map((item) => {
        const itemObj = items.find((i) => Number(i.id) === item.itemId);
        return itemObj?.name || "-";
      })
      .join(", ");

    const updatedOptimistic = {
      ...editRow,
      ...apiPayload,
      warehouseName:
        warehouses.find((w) => Number(w.id) === Number(apiPayload.warehouseId))
          ?.name || editRow.warehouseName,
      userName:
        users.find((u) => Number(u.id) === Number(apiPayload.userId))?.name ||
        editRow.userName,
      returnDate: payload.returnDate
        ? new Date(payload.returnDate).toISOString()
        : null,
      // For table display
      itemName: updatedItemNames,
      ReturnItems: apiPayload.items.map((item, index) => ({
        ...item,
        id: editRow.ReturnItems?.[index]?.id || Date.now() + index, // Preserve existing IDs or create temporary ones
        Item: items.find((i) => Number(i.id) === item.itemId) || {
          name: "Unknown",
        },
      })),
    };

    setRows((prev) =>
      prev.map((r) => (r.id === editRow.id ? updatedOptimistic : r))
    );

    // Send the update with the correct structure
    const { error } = await putJSON(`/api/return/${editRow.id}`, apiPayload);
    setSaving(false);

    if (error) {
      // rollback on error
      setRows(prevRows);
      setToast({ open: true, msg: `Update failed: ${error}`, sev: "error" });
      return;
    }

    setToast({
      open: true,
      msg: "Return updated successfully",
      sev: "success",
    });
    setEditOpen(false);
    setEditRow(null);

    // refresh for canonical data
    await load();
  };
  // inline status change from table
  const handleStatusChange = async (row, newStatus) => {
    if (!row?.id) return;

    if (row.status !== "pending") {
      setToast({
        open: true,
        msg: "Status already changed.",
        sev: "error",
      });
      return;
    }

    // Build payload using row values + new status
    const payload = {
      status: newStatus,
    };

    // optimistic
    const prevRows = [...rows];
    setRows((prev) =>
      prev.map((r) => (r.id === row.id ? { ...r, status: newStatus } : r))
    );

    const { error } = await putJSON(`/api/return/${row.id}`, payload);
    if (error) {
      setRows(prevRows);
      setToast({ open: true, msg: "Failed to update status", sev: "error" });
      return;
    }
    setToast({ open: true, msg: `Status set to ${newStatus}`, sev: "success" });
    await load();
  };

  // delete (optimistic + refetch)
  const handleDelete = async () => {
    if (!deleteRow?.id) return;
    setDeleting(true);

    // optimistic removal
    const prevRows = [...rows];
    setRows((prev) => prev.filter((r) => r.id !== deleteRow.id));

    const { data, error } = await deleteJSON(`/api/return/${deleteRow.id}`);
    setDeleting(false);

    if (error) {
      // rollback
      setRows(prevRows);
      setToast({ open: true, msg: "Delete failed", sev: "error" });
      return;
    }

    setToast({
      open: true,
      msg: data?.message || "Return deleted successfully",
      sev: "success",
    });
    setDeleteRow(null);

    // refresh list
    await load();
  };

  // columns to display in table
  const columns = [
    {
      field: "no",
      headerName: "No.",
      width: 80,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      valueGetter: (params) => {
        const visibleIndex = params.api.getRowIndexRelativeToVisibleRows(
          params.id
        );
        const pm = params.api?.state?.pagination?.paginationModel || {
          page: 0,
          pageSize: rows.length || 0,
        };
        const pageOffset = (pm.page || 0) * (pm.pageSize || 0);
        return pageOffset + visibleIndex + 1;
      },
    },
    {
      field: "userName",
      headerName: "User",
      flex: 1,
      minWidth: 140,
      valueGetter: (params) => params.row.User?.fullName || "-",
    },
    {
      field: "warehouseName",
      headerName: "Warehouse",
      flex: 1,
      minWidth: 160,
      valueGetter: (params) => params.row.Warehouse?.name || "-",
    },
    {
      field: "reason",
      headerName: "Reason",
      flex: 1,
      minWidth: 200,
    },
    {
      field: "status",
      headerName: "Status",
      width: 150,
      sortable: false,
      renderCell: (params) => {
        const row = params.row;
        const colorMap = {
          pending: "warning",
          approved: "success",
          rejected: "error",
        };
        const label = (row.status || "pending").toLowerCase();
        return (
          <Button
            size="small"
            variant="outlined"
            color={colorMap[label] || "inherit"}
            onClick={(e) => setStatusMenu({ anchorEl: e.currentTarget, row })}
          >
            {label}
          </Button>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 170,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const row = params.row;
        return (
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <Tooltip title="Details">
              <IconButton size="small" onClick={() => setDetailRow(row)}>
                <InfoOutlinedIcon />
              </IconButton>
            </Tooltip>

            {canEdit && (
              <Tooltip title="Edit">
                <IconButton
                  size="small"
                  onClick={() => {
                    setEditRow(row);
                    // Initialize with items array instead of single itemId and returnQuantity
                    setEditValues({
                      warehouseId: Number(row.warehouseId) || "",
                      userId: Number(row.userId) || "",
                      status: (row.status || "pending").toLowerCase(),
                      reason: row.reason ?? "",
                      description: row.description ?? "",
                      type: row.type ?? "",
                      returnTo: row.returnTo ?? "",
                      returnDate: toDateInput(row.returnDate),
                      items: row.ReturnItems?.map((it) => ({
                        itemId: Number(it.itemId) || "",
                        quantity: Number(it.quantity) || 1,
                      })) || [{ itemId: "", quantity: 1 }], // Default to one empty item if none exist
                    });
                    setEditOpen(true);
                  }}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
            )}

            <Tooltip title="Delete">
              <IconButton
                size="small"
                color="error"
                onClick={() => setDeleteRow(row)}
              >
                <DeleteOutlineIcon />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  // grid look (theme-consistent)
  const gridSx = {
    "& .MuiDataGrid-root": { border: "none" },
    "& .MuiDataGrid-cell": { borderBottom: "none" },
    "& .MuiDataGrid-columnHeaders": {
      backgroundColor: colors.blueAccent[700],
      borderBottom: "none",
    },
    "& .MuiDataGrid-virtualScroller": {
      backgroundColor: colors.primary[400],
    },
    "& .MuiDataGrid-footerContainer": {
      borderTop: "none",
      backgroundColor: colors.blueAccent[700],
    },
    "& .MuiCheckbox-root": {
      color: `${colors.greenAccent[200]} !important`,
    },
    "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
      color: `${colors.grey[100]} !important`,
    },
  };

  return (
    <>
      <Box m="20px">
        <Header title="RETURNS" subtitle="View, edit, and manage returns" />

        <Box m="40px 0 0 0">
          <SimpleTable
            rows={rows}
            columns={columns}
            loading={loading}
            height="75vh"
            getRowId={(r) => r.id}
            sx={gridSx}
          />
        </Box>
      </Box>

      {/* Status Menu */}
      <Menu
        anchorEl={statusMenu.anchorEl}
        open={Boolean(statusMenu.anchorEl)}
        onClose={() => setStatusMenu({ anchorEl: null, row: null })}
      >
        {["approved", "rejected"].map((opt) => (
          <MenuItem
            key={opt}
            disabled={isSalesMan}
            onClick={async () => {
              if (isSalesMan) return; // block clicks
              const row = statusMenu.row;
              setStatusMenu({ anchorEl: null, row: null });
              await handleStatusChange(row, opt);
            }}
          >
            {opt}
          </MenuItem>
        ))}
      </Menu>

      {/* Details */}
      <Dialog
        open={Boolean(detailRow)}
        onClose={() => setDetailRow(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Return Details</DialogTitle>
        <DialogContent dividers>
          {detailRow && (
            <Box display="flex" flexDirection="column" gap={1.5}>
              <Typography>
                <b>User:</b> {detailRow.User?.fullName || "—"}
              </Typography>
              <Typography>
                <b>Warehouse:</b> {detailRow.Warehouse?.name || "—"}
              </Typography>
              <Typography>
                <b>Reason:</b> {detailRow.reason || "—"}
              </Typography>
              <Typography>
                <b>Status:</b> {detailRow.status || "—"}
              </Typography>
              <Typography>
                <b>Type:</b> {detailRow.type || "—"}
              </Typography>
              <Typography>
                <b>Description:</b> {detailRow.description || "—"}
              </Typography>
              <Typography>
                <b>Return Date:</b> {fmtDate(detailRow.returnDate)}
              </Typography>
              <Typography>
                <b>Return To:</b> {detailRow.returnTo || "—"}
              </Typography>

              {/* Return Items */}
              {Array.isArray(detailRow.ReturnItems) &&
                detailRow.ReturnItems.length > 0 && (
                  <>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle1">
                      <b>Items</b>
                    </Typography>
                    <Box
                      display="grid"
                      gridTemplateColumns="1fr 1fr"
                      gap={2}
                      mt={1}
                    >
                      {detailRow.ReturnItems.map((item) => (
                        <Box
                          key={item.id}
                          p={1}
                          border="1px solid #ddd"
                          borderRadius={1}
                          display="flex"
                          flexDirection="column"
                          gap={0.5}
                        >
                          <Typography>
                            <b>Name:</b> {item.Item?.name || "—"}
                          </Typography>
                          <Typography>
                            <b>Quantity:</b> {Number(item.quantity || 0)}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </>
                )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailRow(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit */}
      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Return</DialogTitle>
        <DialogContent dividers>
          {editRow && (
            <Formik
              initialValues={editValues}
              onSubmit={async (values, { setSubmitting }) => {
                await handleUpdate(values);
                setSubmitting(false);
              }}
            >
              {(bag) => (
                <form onSubmit={bag.handleSubmit}>
                  <Box display="grid" gap={2} sx={{ mt: 1 }}>
                    {/* User Dropdown */}
                    <TextField
                      select
                      label="User"
                      name="userId"
                      value={bag.values.userId}
                      onChange={bag.handleChange}
                      fullWidth
                      variant="filled"
                    >
                      {ensureOption(users, bag.values.userId).map((u) => (
                        <MenuItem key={u.id} value={Number(u.id)}>
                          {u.name}
                        </MenuItem>
                      ))}
                    </TextField>

                    {/* Warehouse Dropdown */}
                    <TextField
                      select
                      label="Warehouse"
                      name="warehouseId"
                      value={bag.values.warehouseId}
                      onChange={bag.handleChange}
                      fullWidth
                      variant="filled"
                    >
                      {ensureOption(warehouses, bag.values.warehouseId).map(
                        (w) => (
                          <MenuItem key={w.id} value={Number(w.id)}>
                            {w.name}
                          </MenuItem>
                        )
                      )}
                    </TextField>

                    <TextField
                      label="Reason"
                      name="reason"
                      value={bag.values.reason}
                      onChange={bag.handleChange}
                      fullWidth
                      variant="filled"
                    />
                    <TextField
                      select
                      label="Return To"
                      name="returnTo"
                      value={bag.values.returnTo}
                      onChange={bag.handleChange}
                      fullWidth
                      variant="filled"
                    >
                      <MenuItem value="store">Store</MenuItem>
                      <MenuItem value="car">Car</MenuItem>
                    </TextField>

                    <TextField
                      label="Description"
                      name="description"
                      value={bag.values.description}
                      onChange={bag.handleChange}
                      fullWidth
                      variant="filled"
                      multiline
                      minRows={3}
                    />
                    <TextField
                      select
                      label="Return Type"
                      name="type"
                      value={bag.values.type}
                      onChange={bag.handleChange}
                      fullWidth
                      variant="filled"
                    >
                      {["Sales Return", "Purchase Return"].map((t) => (
                        <MenuItem key={t} value={t}>
                          {t.charAt(0).toUpperCase() + t.slice(1)}
                        </MenuItem>
                      ))}
                    </TextField>

                    <TextField
                      label="Return Date"
                      type="date"
                      name="returnDate"
                      value={bag.values.returnDate}
                      onChange={bag.handleChange}
                      fullWidth
                      variant="filled"
                      InputLabelProps={{ shrink: true }}
                    />

                    {/* Items Array */}
                    <FieldArray name="items">
                      {({ push, remove }) => (
                        <Box>
                          {bag.values.items.map((it, idx) => (
                            <Box
                              key={idx}
                              display="grid"
                              gridTemplateColumns="2fr 1fr 80px"
                              gap={1}
                              mb={1}
                            >
                              <TextField
                                select
                                label="Item"
                                name={`items[${idx}].itemId`}
                                value={it.itemId}
                                onChange={bag.handleChange}
                                variant="filled"
                                fullWidth
                              >
                                {ensureOption(items, it.itemId).map((m) => (
                                  <MenuItem key={m.id} value={Number(m.id)}>
                                    {m.name}
                                  </MenuItem>
                                ))}
                              </TextField>
                              <TextField
                                type="number"
                                label="Quantity"
                                name={`items[${idx}].quantity`}
                                value={it.quantity}
                                onChange={bag.handleChange}
                                variant="filled"
                              />
                              <Button
                                type="button"
                                color="error"
                                onClick={() => remove(idx)}
                              >
                                X
                              </Button>
                            </Box>
                          ))}
                          <Button
                            type="button"
                            onClick={() => push({ itemId: "", quantity: 1 })}
                          >
                            Add Item
                          </Button>
                        </Box>
                      )}
                    </FieldArray>
                  </Box>

                  <Box display="flex" justifyContent="end" mt={2} gap={2}>
                    <Button
                      type="button"
                      onClick={() => bag.resetForm()}
                      variant="outlined"
                    >
                      Reset
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      color="secondary"
                      disabled={bag.isSubmitting}
                    >
                      {bag.isSubmitting ? "Saving..." : "Save Changes"}
                    </Button>
                  </Box>
                </form>
              )}
            </Formik>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog
        open={!!deleteRow}
        onClose={() => setDeleteRow(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Return</DialogTitle>
        <DialogContent dividers>
          <Typography>Are you sure you want to delete it?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteRow(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            disabled={deleting}
            color="error"
            variant="contained"
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ mt: 5 }}
      >
        <Alert
          onClose={() => setToast((s) => ({ ...s, open: false }))}
          severity={toast.sev}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </>
  );
}
